import { useState } from "react";

const COMMON_SLIDES = [
  {
    emoji: "✨",
    title: "Welcome to Quit the Quit!",
    body: "This is your daily habit game. Every day you log your habits, earn XP, and build streaks. Let's walk you through how it works.",
    color: "#e0f2fe",
    border: "#7dd3fc",
  },
  {
    emoji: "🏠",
    title: "Your Dashboard",
    body: "Each day, tap the habit level you completed for Nutrition 🍓, Movement 🚶‍♀️, and Mindset 🌙.\n\n• Keep the Streak — the minimum to protect your streak\n• Main Quest — your target for the day\n• Extra Credit — going above and beyond\n\nUse the arrows to view past days.",
    color: "#f0fdf4",
    border: "#86efac",
  },
  {
    emoji: "⭐",
    title: "Weekly Challenge",
    body: "At the top of your dashboard is a rotating Weekly Challenge. Complete it and tap to mark it done for bonus XP. It resets every week.",
    color: "#fef9c3",
    border: "#fde047",
  },
  {
    emoji: "💎",
    title: "XP & Levels",
    body: "Every habit you log earns XP. XP fills your level bar — 500 XP per level.\n\nYour level and XP are shown in the header card at the top of every screen.",
    color: "#ede9fe",
    border: "#c4b5fd",
  },
  {
    emoji: "🔒",
    title: "Missed Days",
    body: "If you miss a full day, a \"Resolve Missed Days\" banner appears.\n\n• Revive a full missed day for 25 XP\n• Spend a token on a single missed category for 10 XP\n• No XP? Accept the challenge — complete today's habits and the cost is covered automatically.",
    color: "#fff1f2",
    border: "#fca5a5",
  },
  {
    emoji: "🛡️",
    title: "Streak Shield",
    body: "Complete 14 consecutive full quest days and you'll earn a Streak Shield.\n\nIf you miss a full day while holding a shield, it auto-activates — your streak is protected and the day is covered. Then you start earning the next shield.",
    color: "#fef9c3",
    border: "#fde047",
  },
  {
    emoji: "✨",
    title: "Quests & Side Quests",
    body: "The Quests tab has:\n\n• Weekly Quest Assignments — reflection prompts from your coach (submit for bonus XP)\n• Side Quests — one-off bonus tasks for extra XP\n\nSchedule side quests to appear on your dashboard automatically.",
    color: "#f0fdf4",
    border: "#86efac",
  },
  {
    emoji: "📊",
    title: "You",
    body: "Your personal hub. View your streaks, badges, and shield status.\n\nLog body metrics (weight, measurements, steps) and progress photos. Your habit heatmap shows your consistency at a glance.",
    color: "#e0f2fe",
    border: "#7dd3fc",
  },
  {
    emoji: "💬",
    title: "Community & Messages",
    body: "• Feed — share wins, ask questions, or check in with the group\n• Messages — private chat with your coach\n\nYou'll see notification badges when there's something new.",
    color: "#ede9fe",
    border: "#c4b5fd",
  },
  {
    emoji: "⚙️",
    title: "Customize Your Quest",
    body: "Tap the ⚙️ gear icon to:\n\n• Edit habit names to match your personal goals\n• Toggle meal photo logging, hand portion tracking, and water tracking\n• Set notification reminders\n\nMake it yours.",
    color: "#f0fdf4",
    border: "#86efac",
  },
];

const MOMENTUM_EXTRA_SLIDES = [
  {
    emoji: "🏋️",
    title: "Workouts",
    body: "The Workouts tab lets you log your training sessions and build a workout schedule.\n\n• Browse on-demand workout videos\n• Log completed workouts for automatic XP and side quest credit\n• Schedule workouts to appear as reminders on your dashboard\n• Track your personal records and history",
    color: "#f0fdf4",
    border: "#86efac",
    insertAfter: "Streak Shield",
  },
  {
    emoji: "🏆",
    title: "Challenges",
    body: "Join group Challenges from the Community tab.\n\n• Compete in race challenges — first to hit the target wins\n• Work together in collective challenges — the whole group wins\n• Log progress manually or let it track automatically from your workouts and habits\n• Pin challenges to your dashboard to watch your rank in real time",
    color: "#fef9c3",
    border: "#fde047",
    insertAfter: "Community & Messages",
  },
];

const READY_SLIDE = {
  emoji: "🚀",
  title: "You're ready!",
  body: "That's everything you need to know. Log your first habit today and start building your streak.\n\nYou've got this. Let's go! 💪",
  color: "#e0f2fe",
  border: "#7dd3fc",
};

function buildSlides(programTag) {
  const isMomentum = programTag === "momentum";
  const slides = [...COMMON_SLIDES];

  if (isMomentum) {
    // Insert Workouts slide after Streak Shield
    const shieldIdx = slides.findIndex((s) => s.title === "Streak Shield");
    if (shieldIdx >= 0) slides.splice(shieldIdx + 1, 0, MOMENTUM_EXTRA_SLIDES[0]);

    // Insert Challenges slide after Community & Messages
    const communityIdx = slides.findIndex((s) => s.title === "Community & Messages");
    if (communityIdx >= 0) slides.splice(communityIdx + 1, 0, MOMENTUM_EXTRA_SLIDES[1]);
  }

  slides.push(READY_SLIDE);
  return slides;
}

export default function OnboardingWalkthrough({ onComplete, onSkip, isPreview = false, programTag = "momentum" }) {
  const slides = buildSlides(programTag);
  const [step, setStep] = useState(0);
  const slide = slides[step];
  const isLast = step === slides.length - 1;

  function next() {
    if (isLast) {
      onComplete?.();
    } else {
      setStep((s) => s + 1);
    }
  }

  function prev() {
    setStep((s) => Math.max(0, s - 1));
  }

  return (
    <div style={{
      position: "fixed",
      inset: 0,
      background: "rgba(30, 58, 138, 0.55)",
      zIndex: 9000,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "20px",
      boxSizing: "border-box",
    }}>
      <div style={{
        width: "100%",
        maxWidth: "480px",
        background: "#f5faff",
        borderRadius: "28px",
        padding: "32px 28px 24px",
        border: "2px solid #dbeafe",
        boxShadow: "0 20px 60px rgba(30, 58, 138, 0.22)",
        display: "flex",
        flexDirection: "column",
        gap: "0",
        maxHeight: "90vh",
        overflowY: "auto",
        boxSizing: "border-box",
      }}>
        {/* Progress dots */}
        <div style={{ display: "flex", gap: "6px", justifyContent: "center", marginBottom: "24px" }}>
          {slides.map((_, i) => (
            <div
              key={i}
              onClick={() => setStep(i)}
              style={{
                width: i === step ? "20px" : "8px",
                height: "8px",
                borderRadius: "999px",
                background: i === step ? "#2563eb" : "#bfdbfe",
                cursor: "pointer",
                transition: "all 0.2s",
                flexShrink: 0,
              }}
            />
          ))}
        </div>

        {/* Slide content */}
        <div style={{
          background: slide.color,
          border: `2px solid ${slide.border}`,
          borderRadius: "22px",
          padding: "28px 24px",
          textAlign: "center",
          marginBottom: "24px",
          flex: 1,
        }}>
          <div style={{ fontSize: "52px", marginBottom: "16px", lineHeight: 1 }}>
            {slide.emoji}
          </div>
          <div style={{
            fontWeight: "bold",
            color: "#1e3a5f",
            fontSize: "20px",
            marginBottom: "14px",
            lineHeight: 1.3,
          }}>
            {slide.title}
          </div>
          <div style={{
            color: "#4b6b8a",
            fontSize: "14px",
            lineHeight: 1.7,
            whiteSpace: "pre-line",
            textAlign: "left",
          }}>
            {slide.body}
          </div>
        </div>

        {/* Step counter */}
        <div style={{ textAlign: "center", color: "#6b8cb0", fontSize: "12px", marginBottom: "16px" }}>
          {step + 1} of {slides.length}
        </div>

        {/* Navigation */}
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          {step > 0 && (
            <button
              type="button"
              onClick={prev}
              style={{
                padding: "12px 18px",
                borderRadius: "14px",
                border: "2px solid #bfdbfe",
                background: "#f5faff",
                color: "#4b6b8a",
                fontWeight: "bold",
                cursor: "pointer",
                fontSize: "14px",
                flexShrink: 0,
              }}
            >
              ← Back
            </button>
          )}

          <button
            type="button"
            onClick={next}
            style={{
              flex: 1,
              padding: "14px",
              borderRadius: "14px",
              border: "none",
              background: isLast ? "#2563eb" : "#38bdf8",
              color: "#fff",
              fontWeight: "bold",
              fontSize: "15px",
              cursor: "pointer",
              boxShadow: "0 6px 16px rgba(37, 99, 235, 0.3)",
            }}
          >
            {isLast
              ? isPreview ? "Close Preview" : "Get Started 🚀"
              : "Next →"}
          </button>

          {!isLast && !isPreview && onSkip && (
            <button
              type="button"
              onClick={onSkip}
              style={{
                padding: "12px 14px",
                borderRadius: "14px",
                border: "2px solid #bfdbfe",
                background: "#f5faff",
                color: "#6b8cb0",
                fontWeight: "bold",
                cursor: "pointer",
                fontSize: "13px",
                flexShrink: 0,
              }}
            >
              Skip
            </button>
          )}

          {isPreview && !isLast && (
            <button
              type="button"
              onClick={() => onComplete?.()}
              style={{
                padding: "12px 14px",
                borderRadius: "14px",
                border: "2px solid #bfdbfe",
                background: "#f5faff",
                color: "#6b8cb0",
                fontWeight: "bold",
                cursor: "pointer",
                fontSize: "13px",
                flexShrink: 0,
              }}
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
